import React from 'react';
import RouteErrorBoundary from '@app/components/ErrorBoundary';
import ChildLayout from '@app/layouts/ChildLayout';
import DefaultLayout from '@app/layouts/DefaultLayout';
import DefaultLayoutNoSideBar from '@app/layouts/DefaultLayoutNoSideBar';
import EmptyLayout from '@app/layouts/EmptyLayout';
import { createBrowserRouter, RouteObject } from 'react-router-dom';
import { CONFIGURATION_ROUTE_COMMON, HOME_PATH, RouterAccessPermission } from '../common-route';
import PrivateRoute from '../components/PrivateRoute';
import PublicRoute from '../components/PublicRoute';
import { PrivateAndPublicRouteProps, ScreenLayout } from '../types';

/**
 * Builds the router configuration object based on the given app routes.
 *
 * @param appRoutes - The app routes.
 * @returns The router configuration object.
 */
const buildRouterItem = (appRoutes: AppRoute[]) => {
  const layoutRoutes: Record<ScreenLayout, RouteObject> = {
    [ScreenLayout.Empty]: { element: <EmptyLayout />, children: [] },
    [ScreenLayout.Default]: { element: <DefaultLayout />, children: [] },
    [ScreenLayout.DefaultNoSideBar]: { element: <DefaultLayoutNoSideBar />, children: [] },
    [ScreenLayout.Child]: { element: <ChildLayout />, children: [] },
  };

  appRoutes.forEach((route) => {
    const { layout, element, path, title, screenId, permission } = route;
    const routerProps: PrivateAndPublicRouteProps = {
      Component: element,
      title,
      screenId,
    };
    const routeElement =
      permission === RouterAccessPermission.Public ? (
        <PublicRoute {...routerProps} />
      ) : (
        <PrivateRoute {...routerProps} />
      );
    layoutRoutes[layout as ScreenLayout].children?.push({
      element: routeElement,
      path,
      errorElement: <RouteErrorBoundary />,
    });
  });

  const mainRoutes = Object.values(layoutRoutes).filter(
    (routeLayout) => routeLayout.children && routeLayout.children.length > 0
  );
  const routers = [
    ...CONFIGURATION_ROUTE_COMMON,
    { path: HOME_PATH, children: mainRoutes, errorElement: <RouteErrorBoundary /> },
  ];
  return createBrowserRouter(routers);
};

export default buildRouterItem;
